import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import { RobotDevice } from '../../types';
import { getRobotSettings, updateRobotSettings } from '../api';
import { robotInfoKeys } from '../queries';
import { RobotInfo, RobotSettings } from '../types';

export const useRobotSetting = <TData = boolean>(
  robot: RobotDevice | undefined,
  settingName: keyof RobotSettings,
  options?: {
    query?: UseQueryOptions<
      boolean | undefined,
      unknown,
      TData | undefined,
      ReturnType<typeof robotInfoKeys.setting>
    >;
    mutation?: UseMutationOptions<
      void,
      unknown,
      boolean,
      { previousValue: unknown }
    >;
  }
) => {
  const queryClient = useQueryClient();
  const robotId = robot?.id ?? '';

  const query = useQuery({
    queryKey: robotInfoKeys.setting(robotId, settingName),
    queryFn: async () => {
      const res = await getRobotSettings(robot?.websocketConnection?.ipAddress);
      return res?.[settingName];
    },
    enabled:
      robot?.id !== undefined &&
      robot?.websocketConnection?.ipAddress !== undefined,

    // Get the values that may already exist from getRobotInfo
    initialData: () =>
      queryClient.getQueryData<RobotInfo>(robotInfoKeys.all(robotId))
        ?.settings?.[settingName],
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(robotInfoKeys.all(robotId))?.dataUpdatedAt,

    ...options?.query,
  });

  const mutation = useMutation({
    mutationFn: async (newValue: boolean) => {
      const res = await updateRobotSettings(
        robot?.websocketConnection?.ipAddress,
        { [settingName]: newValue }
      );

      const success = res?.[settingName] ?? false;
      if (!success) {
        throw Error('Updating setting returned unsuccessful result');
      }
    },
    onMutate: async (newValue) => {
      const queryKey = robotInfoKeys.setting(robotId, settingName);

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries(queryKey);

      // Snapshot the previous value
      const previousValue = queryClient.getQueryData(queryKey);

      // Optimistically update to the new value
      queryClient.setQueryData(queryKey, newValue);

      // Return a context with the previous value
      return { previousValue };
    },
    onSettled: (_data, error, _variables, context) => {
      if (error) {
        queryClient.setQueryData(
          robotInfoKeys.setting(robotId, settingName),
          context?.previousValue
        );
      }

      // Invalidate all queries that may contain stale data due to this setting update
      queryClient.invalidateQueries(robotInfoKeys.all(robotId));
    },
    ...options?.mutation,
  });

  return { query, mutation };
};
